(() => {
  'use strict';
  const start = () => {
    window.EmyliaMobile?.destroy?.();
    const lifetime = new AbortController();
    const signal = lifetime.signal;
    const root = document.documentElement;
    const body = document.body;
    const mobile = matchMedia('(max-width: 749px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const connection = navigator.connection;
    const editor = Boolean(window.Shopify?.designMode);
    const main = document.querySelector('main, #MainContent, .content-for-layout');
    if (!main) return;
    const observers = [];
    const videos = new Map();
    const logoOwners = new Set();
    const animations = new Set();
    const generatedIds = new Map();
    const revealed = new WeakSet();
    let dock, dialog, progress, dockLifetime, header;
    let frame = 0;
    let dockHeight = 96;
    let pendingTarget = null;
    let headerObserver, bodyObserver, videoObserver, revealObserver;
    const listen = (target, type, handler, options = {}) => {
      target?.addEventListener(type, handler, { ...options, signal });
    };
    const visible = element => element.isConnected && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    const saveData = () => Boolean(connection?.saveData);
    const editable = element => element instanceof Element && Boolean(element.closest('input:not([type="checkbox"],[type="radio"],[type="button"],[type="submit"]),textarea,select,[contenteditable="true"]'));
    const schedule = () => {
      if (!frame && !signal.aborted) frame = requestAnimationFrame(update);
    };
    const update = () => {
      frame = 0;
      if (signal.aborted) return;
      const range = Math.max(0, root.scrollHeight - innerHeight);
      const y = Math.max(0, window.scrollY);
      if (progress) progress.style.transform = `scaleX(${range > 0 ? Math.min(1, y / range) : 0})`;
      if (!dock) return;
      const zoomed = (window.visualViewport?.scale || 1) > 1.1;
      const hide = !mobile.matches || editable(document.activeElement) || zoomed || range < innerHeight * .6;
      if (dock.hidden !== hide) dock.hidden = hide;
      if (!hide) {
        const measured = Math.ceil(dock.getBoundingClientRect().height + 24);
        if (measured !== dockHeight) {
          dockHeight = measured;
          root.style.setProperty('--em-dock-space', `${dockHeight}px`);
        }
      }
    };
    const measureHeader = () => {
      const next = document.querySelector('header-component, #shopify-section-header .header, .header');
      if (next !== header) {
        if (header) headerObserver?.unobserve(header);
        header = next;
        if (header) headerObserver?.observe(header);
      }
      if (header) {
        const height = Math.round(header.getBoundingClientRect().height);
        if (height > 0) root.style.setProperty('--em-header-height', `${height}px`);
      }
    };
    const allowedVideo = state => {
      if (document.hidden || reduced.matches || saveData()) return false;
      if (!state.logo && mobile.matches) return false;
      return state.inView && state.wanted;
    };
    const syncVideo = (video, state) => {
      if (!video.isConnected) {
        videoObserver?.unobserve(video);
        videos.delete(video);
        return;
      }
      if (state.logo) state.logo.toggleAttribute('data-em-logo-static', reduced.matches || saveData());
      if (!allowedVideo(state)) {
        if (!video.paused) video.pause();
        return;
      }
      if (video.paused) video.play()?.catch(() => {});
    };
    const syncVideos = () => videos.forEach((state, video) => syncVideo(video, state));
    const prepareLogo = video => {
      const owner = video.closest('.header__heading-link,.header__logo,header-logo,.header-logo,a') || video.parentElement;
      if (!owner) return null;
      owner.setAttribute('data-em-logo', '');
      logoOwners.add(owner);
      const fallback = owner.querySelector('img,.header__logo-fallback,.em-logo-text');
      if (!fallback && !video.poster) {
        const text = document.createElement('span');
        text.className = 'em-logo-text';
        text.textContent = 'EMYLIA.AI';
        owner.append(text);
      }
      if (video.poster && !fallback) {
        const image = document.createElement('img');
        image.className = 'em-logo-poster';
        image.src = video.poster;
        image.alt = 'Emylia';
        image.width = 76;
        image.height = 76;
        owner.append(image);
      }
      owner.toggleAttribute('data-em-logo-playing', !video.paused && video.readyState >= 2);
      listen(video, 'playing', () => owner.setAttribute('data-em-logo-playing', ''));
      listen(video, 'error', () => owner.removeAttribute('data-em-logo-playing'));
      return owner;
    };
    const scanVideos = scope => {
      const selector = 'video.bg-video,.bg-video-wrap video,video.header-video,video.menu-drawer-video,video.menu-drawer-bg-video,.menu-drawer-video-wrap video,video[data-em-decorative],video.header__logo-video';
      const matches = [...scope.querySelectorAll(selector)];
      if (scope.matches?.(selector)) matches.unshift(scope);
      matches.forEach(video => {
        if (videos.has(video) || video.controls || !video.muted) return;
        const logo = video.matches('.header__logo-video') ? prepareLogo(video) : null;
        const state = { logo, wanted: video.autoplay || !video.paused, inView: visible(video) };
        videos.set(video, state);
        listen(video, 'play', () => {
          if (!allowedVideo(state)) video.pause();
        });
        videoObserver?.observe(video);
        syncVideo(video, state);
      });
    };
    const scanReveals = scope => {
      if (editor || reduced.matches || saveData() || !revealObserver) return;
      const matches = [...scope.querySelectorAll('.em-reveal,[data-em-reveal]')];
      matches.forEach(element => {
        if (revealed.has(element)) return;
        if (element.getBoundingClientRect().top < innerHeight) revealed.add(element);
        else revealObserver.observe(element);
      });
    };
    const clearTools = () => {
      dockLifetime?.abort();
      if (dialog?.open) dialog.close();
      dock?.remove();
      dialog?.remove();
      progress?.remove();
      dock = dialog = progress = undefined;
      pendingTarget = null;
      body.classList.remove('em-mobile-tools');
    };
    const buildTools = () => {
      clearTools();
      if (editor || !mobile.matches || body.dataset.emMobileTools === 'off') return;
      if (/\/(?:cart|checkout|checkouts|account|products)(?:\/|$)/.test(location.pathname)) return;
      if (main.querySelector('form[action*="contact"],form[action*="cart"],product-form-component')) return;
      const contact = [...main.querySelectorAll('a[href]')].find(link => {
        try {
          const url = new URL(link.href, location.href);
          return url.origin === location.origin && /\/pages\/contact\/?$/.test(url.pathname) && url.pathname !== location.pathname;
        } catch { return false; }
      });
      const headings = [...main.querySelectorAll('h2')].filter(heading => {
        if (!visible(heading) || heading.closest('dialog,details,.product-card,.collection-card,.resource-card,slideshow-component')) return false;
        return heading.textContent.trim().length > 0;
      }).slice(0, 12);
      if (!contact || headings.length < 3 || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
      dockLifetime = new AbortController();
      const dockSignal = dockLifetime.signal;
      const uniqueId = prefix => {
        let index = 1;
        while (document.getElementById(`${prefix}-${index}`)) index++;
        return `${prefix}-${index}`;
      };
      dialog = document.createElement('dialog');
      dialog.className = 'em-section-dialog';
      dialog.id = uniqueId('em-section-dialog');
      const head = document.createElement('div');
      head.className = 'em-dialog-head';
      const title = document.createElement('h2');
      title.className = 'em-dialog-title';
      title.id = uniqueId('em-section-title');
      title.textContent = 'On this page';
      dialog.setAttribute('aria-labelledby', title.id);
      const close = document.createElement('button');
      close.className = 'em-dialog-close';
      close.type = 'button';
      close.setAttribute('aria-label', 'Close page navigation');
      close.textContent = '×';
      close.addEventListener('click', () => dialog.close(), { signal: dockSignal });
      head.append(title, close);
      const list = document.createElement('ol');
      headings.forEach((heading, index) => {
        if (!heading.id) {
          const id = uniqueId('em-section');
          generatedIds.set(heading, id);
          heading.id = id;
        }
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${encodeURIComponent(heading.id)}`;
        const number = document.createElement('span');
        number.className = 'em-section-number';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(index + 1).padStart(2, '0');
        const label = document.createElement('span');
        label.textContent = heading.textContent.replace(/\s+/g, ' ').trim();
        link.append(number, label);
        link.addEventListener('click', event => {
          if (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          pendingTarget = heading;
          dialog.close();
        }, { signal: dockSignal });
        item.append(link);
        list.append(item);
      });
      dialog.append(head, list);
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
      }, { signal: dockSignal });
      dock = document.createElement('nav');
      dock.className = 'em-mobile-dock';
      dock.setAttribute('aria-label', 'Page shortcuts');
      const explore = document.createElement('button');
      explore.type = 'button';
      explore.textContent = 'On this page';
      explore.setAttribute('aria-haspopup', 'dialog');
      explore.setAttribute('aria-controls', dialog.id);
      explore.setAttribute('aria-expanded', 'false');
      explore.addEventListener('click', () => {
        if (!dialog.open) dialog.showModal();
        explore.setAttribute('aria-expanded', 'true');
      }, { signal: dockSignal });
      dialog.addEventListener('close', () => {
        explore.setAttribute('aria-expanded', 'false');
        if (pendingTarget?.isConnected) {
          const target = pendingTarget;
          pendingTarget = null;
          const temporary = !target.hasAttribute('tabindex');
          if (temporary) target.setAttribute('tabindex', '-1');
          requestAnimationFrame(() => {
            if (signal.aborted) return;
            target.focus({ preventScroll: true });
            if (temporary) target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true, signal });
          });
        }
      }, { signal: dockSignal });
      const cta = document.createElement('a');
      cta.href = contact.href;
      cta.textContent = 'Let’s talk ↗';
      dock.append(explore, cta);
      progress = document.createElement('div');
      progress.className = 'em-mobile-progress';
      progress.setAttribute('aria-hidden', 'true');
      body.append(dialog, dock, progress);
      body.classList.add('em-mobile-tools');
      schedule();
    };
    if ('ResizeObserver' in window) {
      headerObserver = new ResizeObserver(() => measureHeader());
      bodyObserver = new ResizeObserver(schedule);
      bodyObserver.observe(main);
      observers.push(headerObserver, bodyObserver);
    }
    if ('IntersectionObserver' in window) {
      videoObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        const state = videos.get(entry.target);
        if (state) { state.inView = entry.isIntersecting; syncVideo(entry.target, state); }
      }), { rootMargin: '0px', threshold: .01 });
      revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const element = entry.target;
        revealObserver.unobserve(element);
        revealed.add(element);
        if (reduced.matches || saveData() || typeof element.animate !== 'function') return;
        try {
          const animation = element.animate([{ opacity: .6, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 320, easing: 'cubic-bezier(.2,.7,.2,1)' });
          animations.add(animation);
          animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
        } catch {}
      }), { threshold: .08 });
      observers.push(videoObserver, revealObserver);
    }
    const refresh = () => {
      measureHeader();
      scanVideos(document);
      scanReveals(main);
      syncVideos();
      buildTools();
    };
    listen(window, 'scroll', schedule, { passive: true });
    listen(window, 'resize', () => { measureHeader(); schedule(); }, { passive: true });
    listen(window.visualViewport, 'resize', schedule, { passive: true });
    listen(document, 'focusin', schedule);
    listen(document, 'focusout', schedule);
    listen(document, 'toggle', schedule, { capture: true });
    listen(document, 'visibilitychange', syncVideos);
    listen(window, 'pageshow', () => { syncVideos(); schedule(); });
    listen(mobile, 'change', () => { syncVideos(); buildTools(); });
    listen(reduced, 'change', () => {
      if (reduced.matches) animations.forEach(animation => animation.cancel());
      syncVideos();
      scanReveals(main);
    });
    listen(connection, 'change', syncVideos);
    listen(document, 'shopify:section:load', refresh);
    listen(document, 'shopify:section:unload', () => { syncVideos(); schedule(); });
    window.EmyliaMobile = {
      version: '1.0.0',
      refresh,
      destroy() {
        lifetime.abort();
        cancelAnimationFrame(frame);
        observers.forEach(observer => observer.disconnect());
        animations.forEach(animation => animation.cancel());
        clearTools();
        logoOwners.forEach(owner => {
          owner.removeAttribute('data-em-logo');
          owner.removeAttribute('data-em-logo-static');
          owner.removeAttribute('data-em-logo-playing');
          owner.querySelectorAll('.em-logo-text,.em-logo-poster').forEach(element => element.remove());
        });
        generatedIds.forEach((id, element) => { if (element.id === id) element.removeAttribute('id'); });
        root.style.removeProperty('--em-header-height');
        root.style.removeProperty('--em-dock-space');
        delete window.EmyliaMobile;
      }
    };
    refresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
